import SuperCluster, { ClusterFeature } from "supercluster";
import { AbstractViewportAlgorithm, AlgorithmInput, AlgorithmOutput, ViewportAlgorithmOptions } from "./core";
import { SuperClusterOptions } from "./supercluster";
import * as equal from "fast-deep-equal";
import { DataCluster } from "../DataCluster";
import { getPaddedViewport } from "./utils";
import { MarkerUtils } from "../MarkerUtils";

export interface SuperClusterViewportOptions extends SuperClusterOptions, ViewportAlgorithmOptions {}

export interface SuperClusterViewportState {
    zoom: number;
    view: [number, number, number, number];
}

export class SuperClusterViewportAlgorithm extends AbstractViewportAlgorithm{
    protected superCluster: SuperCluster;
    protected lstPoints: google.maps.Data.Feature[];
    protected clusters: DataCluster[];
    protected state: SuperClusterViewportState;

    constructor({
        maxZoom,
        radius = 60,
        ...options
    }: SuperClusterOptions){
        var viewportPadding = 60;

        super({ maxZoom, viewportPadding });

        this.superCluster = new SuperCluster({
            maxZoom: this.maxZoom,
            radius,
            ...options
        });
        
        this.state = { zoom: -1, view: [0, 0, 0, 0] };
    }

    public calculate(input: AlgorithmInput): AlgorithmOutput {
        const state: SuperClusterViewportState = {
            zoom: Math.round(input.map.getZoom()),
            view: getPaddedViewport(
                input.map.getBounds(),
                input.mapCanvasProjection,
                this.viewportPadding
            )
        };

        let changed = !equal(this.state, state);

        //
        //  Force Redraw used when the Main Map updates the page sees that the points moved
        //      There's some issue with the list of points being passed as a reference
        //      so this.lstPoints already has the Lat and Long change before calculate runs
        //      and this if statement never gets hit
        //
        if(input.ForceRedraw == true || !this.PointsEqual(input.lstPoints, this.lstPoints)){
            changed = true;
            this.lstPoints = [...input.lstPoints];

            const points = this.lstPoints.map((point) => {
                const position = MarkerUtils.getPosition(point);
                const coordinates = [position.lng(), position.lat()];
                return {
                    type: "Feature" as const,
                    geometry: {
                        type: "Point" as const,
                        coordinates
                    },
                    properties: { point }
                };
            });

            this.superCluster.load(points);
        }

        if(changed){
            this.clusters = this.cluster(input);
            this.state = state;
        }

        return { clusters: this.clusters, changed };
    }

    public PointsEqual(lstOne: google.maps.Data.Feature[], lstTwo: google.maps.Data.Feature[]){
        //
        // 1 null, 2 exists
        //
        if(lstOne == null && lstTwo != null){
            return false;
        }
        //
        // 2 null, 1 exists
        //
        else if(lstTwo == null && lstOne != null){
            return false;
        }
        //
        // both null
        //
        else if(lstOne == null && lstTwo == null){
            return true;
        }
        //
        // Different Lengths, can't be equal
        //  0+ != 0+
        else if(lstOne.length != lstTwo.length){
            return false;
        }
        //
        // Both have no items, must be equal
        // 0 = 0
        else if(lstOne.length == 0 && lstTwo.length == 0){
            return true;
        }
        //
        // Check each item
        // 1+ = 1+
        else{
            var AllEqual = true;
            var Type = lstOne[0].getProperty("Type");

            for(var z = 0; z < lstOne.length; z++){
                var ItemOne = lstOne[z];

                var Found: boolean = false;
                var SameLocation = false;

                for(var x = 0; x < lstTwo.length; x++){
                    var ItemTwo = lstTwo[x];

                    if(Type == "Bus"){
                        if(ItemOne.getProperty("BusID") == ItemTwo.getProperty("BusID")){
                            Found = true;
                        }
                    }
                    else if(Type == "Student"){
                        if(ItemOne.getProperty("BusStudentRideTxnID") == ItemTwo.getProperty("BusStudentRideTxnID")){
                            Found = true;
                        }
                    }

                    if(Found == true){
                        if(ItemOne.getProperty("Lat") == ItemTwo.getProperty("Lat") && ItemOne.getProperty("Long") == ItemTwo.getProperty("Long")){
                            SameLocation = true;
                        }
                        break;
                    }
                }

                //
                // All Points must exist in both lists and have same location
                //
                AllEqual = AllEqual && Found && SameLocation;

                // Exit early if one point doesn't match
                if(AllEqual == false){
                    return AllEqual;
                }
            }

            return AllEqual;
        }
    }

    public cluster({ map, mapCanvasProjection }: AlgorithmInput): DataCluster[] {
        const state: SuperClusterViewportState = {
            zoom: Math.round(map.getZoom()),
            view: getPaddedViewport(
                map.getBounds(),
                mapCanvasProjection,
                this.viewportPadding
            )
        };

        return this.superCluster
            .getClusters(state.view, state.zoom)
            .map((feature: ClusterFeature<{ point: google.maps.Data.Feature }>) =>
                this.transformCluster(feature)
            );
    }

    protected transformCluster({
        geometry: {
            coordinates: [lng, lat]
        },
        properties
    }: ClusterFeature<{ point: google.maps.Data.Feature }>): DataCluster{
        if(properties.cluster){
            return new DataCluster({
                lstPoints: this.superCluster
                    .getLeaves(properties.cluster_id, Infinity)
                    .map((leaf) => leaf.properties.point),
                position: new google.maps.LatLng({lat: lat, lng: lng })
            });
        }

        const point = properties.point;

        return new DataCluster({
            lstPoints: [point],
            position: MarkerUtils.getPosition(point)
        });
    }


}